import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { sendPasswordResetEmail } from "../utils/passwordResetHelper";
import {
  User,
  ShieldAlert,
  Settings as SettingsIcon,
  Key,
  LogOut,
  Trash2,
  Sliders,
  Sun,
  Moon,
  Monitor,
  Search,
  Bell,
  ShieldCheck,
  CreditCard,
  Bookmark,
  Eye,
  HelpCircle,
  Check,
  X,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
  Copy,
  Sparkles,
  Database,
  MapPin,
  List,
  Compass,
  FileText,
  RefreshCw,
  Lock,
  Mail,
  CheckCircle2,
  Download,
  LayoutGrid,
  Zap,
  Award,
  Activity,
  Info,
  Layers,
  ChevronDown,
  Globe,
  Navigation,
  CheckSquare,
  AlertCircle,
  BarChart2,
  Shield,
  Save
} from "lucide-react";
import { AudienceSelector } from "./AudienceSelector";
import { ThemeSelector, ThemePreference } from "./ThemeSelector";
import { LanguageSelector } from "./LanguageSelector";
import { AudienceModeId, AUDIENCE_MODES, getAudienceModeConfig } from "../config/audienceModes";

export type SettingsTabId =
  | "account"
  | "profile"
  | "appearance"
  | "search"
  | "notifications"
  | "privacy"
  | "billing"
  | "data"
  | "accessibility"
  | "help"
  | "admin";

export interface SettingsDashboardProps {
  currentUser: any;
  currentPath: string;
  navigateTo: (path: string) => void;
  t: (key: string, fallback?: string) => string;
  locale: string;
  showToast: (msg: string, type?: "success" | "error" | "info") => void;
  apiFetch: (url: string, opts?: any) => Promise<any>;
  isProUser: boolean;
  billingStatus: any;
  handleLogout: () => void;
  savedBusinesses?: any[];
  settingsAppTheme: string;
  handleSaveUserSettings: (theme: string, style: string, lang: string) => void;
  mapStyle: string;
  onMapStyleChange: (style: string) => void;
  settingsLanguage: string;
  setSettingsLanguage: (lang: string) => void;
  settingsDefaultZipcode: string;
  setSettingsDefaultZipcode: (zip: string) => void;
  settingsMaxResults: number;
  setSettingsMaxResults: (num: number) => void;
  trackingEnabled: boolean;
  handleToggleTracking: (enabled: boolean) => Promise<void>;
  audienceMode: AudienceModeId;
  handleSelectAudienceMode: (mode: AudienceModeId) => void;
  adminUsePro: boolean;
  setAdminUsePro: (val: boolean) => void;
  settingsBypassPaywall: boolean;
  setSettingsBypassPaywall: (val: boolean) => void;
  handleLinkIdentity: (provider: "google" | "github") => void;
  onUpgradeClick: () => void;
  onContactSupportClick?: () => void;
  onOpenLegalTab?: (tab: string) => void;
}

export function SettingsDashboard({
  currentUser,
  currentPath,
  navigateTo,
  t,
  locale,
  showToast,
  apiFetch,
  isProUser,
  billingStatus,
  handleLogout,
  savedBusinesses = [],
  settingsAppTheme,
  handleSaveUserSettings,
  mapStyle,
  onMapStyleChange,
  settingsLanguage,
  setSettingsLanguage,
  settingsDefaultZipcode,
  setSettingsDefaultZipcode,
  settingsMaxResults,
  setSettingsMaxResults,
  trackingEnabled,
  handleToggleTracking,
  audienceMode,
  handleSelectAudienceMode,
  adminUsePro,
  setAdminUsePro,
  settingsBypassPaywall,
  setSettingsBypassPaywall,
  handleLinkIdentity,
  onUpgradeClick,
  onContactSupportClick,
  onOpenLegalTab
}: SettingsDashboardProps) {
  // Determine user role and admin status
  const userRole = currentUser?.role || currentUser?.user_metadata?.role || "user";
  const isAdmin = userRole === "admin" || userRole === "dev" || userRole === "support";

  // Tab mapping & URL sync
  const deriveTabFromPath = (path: string): SettingsTabId => {
    const clean = path.toLowerCase().replace(/\/+$/, "");
    if (clean === "/account") return "account";
    const parts = clean.split("/");
    if (parts.length >= 3 && parts[1] === "settings") {
      const tab = parts[2];
      const validTabs: SettingsTabId[] = [
        "account",
        "profile",
        "appearance",
        "search",
        "notifications",
        "privacy",
        "billing",
        "data",
        "accessibility",
        "help",
        "admin"
      ];
      if (validTabs.includes(tab as SettingsTabId)) {
        if (tab === "admin" && !isAdmin) return "account";
        return tab as SettingsTabId;
      }
    }
    return "account";
  };

  const [activeTab, setActiveTab] = useState<SettingsTabId>(() => deriveTabFromPath(currentPath));

  useEffect(() => {
    const tabFromUrl = deriveTabFromPath(currentPath);
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [currentPath]);

  const handleTabChange = (tab: SettingsTabId) => {
    setActiveTab(tab);
    navigateTo(`/settings/${tab}`);
  };

  // Search filter across settings
  const [settingsSearchQuery, setSettingsSearchQuery] = useState("");

  // Account profile state
  const [fullName, setFullName] = useState(
    currentUser?.user_metadata?.full_name || currentUser?.full_name || ""
  );
  const [displayName, setDisplayName] = useState(
    currentUser?.user_metadata?.display_name || currentUser?.display_name || ""
  );
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [hasProfileChanges, setHasProfileChanges] = useState(false);

  // Delete account state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Search & Preferences state
  const [distanceUnit, setDistanceUnit] = useState<"miles" | "km">(
    () => (localStorage.getItem("biznearyou_distance_unit") as "miles" | "km") || "miles"
  );
  const [searchRadius, setSearchRadius] = useState<number>(
    () => Number(localStorage.getItem("biznearyou_search_radius")) || 15
  );
  const [travelMode, setTravelMode] = useState<string>(
    () => localStorage.getItem("biznearyou_travel_mode") || "driving"
  );
  const [defaultSort, setDefaultSort] = useState<string>(
    () => localStorage.getItem("biznearyou_default_sort") || "best_match"
  );
  const [autoApplyPreferences, setAutoApplyPreferences] = useState<boolean>(
    () => localStorage.getItem("biznearyou_auto_apply") !== "false"
  );

  // Appearance state
  const [layoutDensity, setLayoutDensity] = useState<"comfortable" | "compact">(
    () => (localStorage.getItem("biznearyou_compact_mode") === "true" ? "compact" : "comfortable")
  );
  const [reducedMotion, setReducedMotion] = useState<boolean>(
    () => localStorage.getItem("biznearyou_reduced_motion") === "true"
  );
  const [showPhoneDetails, setShowPhoneDetails] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_phone") !== "false"
  );
  const [showGapScore, setShowGapScore] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_gap") !== "false"
  );
  const [rememberCollapsed, setRememberCollapsed] = useState<boolean>(
    () => localStorage.getItem("biznearyou_remember_collapsed") === "true"
  );

  // Search & Discovery Visibility state
  const [showOpportunityScore, setShowOpportunityScore] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_opp_score") !== "false"
  );
  const [showHiringSignals, setShowHiringSignals] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_hiring_signals") !== "false"
  );
  const [showAudienceSignals, setShowAudienceSignals] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_audience_signals") !== "false"
  );
  const [showLowConfidence, setShowLowConfidence] = useState<boolean>(
    () => localStorage.getItem("biznearyou_show_low_confidence") === "true"
  );
  const [defaultViewMode, setDefaultViewMode] = useState<"list" | "map">(
    () => (localStorage.getItem("biznearyou_default_view") as "list" | "map") || "list"
  );

  // Notifications state
  const [notifications, setNotifications] = useState({
    oppAlertsInApp: true,
    oppAlertsEmail: true,
    hiringSignalsInApp: true,
    hiringSignalsEmail: true,
    savedUpdatesInApp: true,
    savedUpdatesEmail: false,
    freshnessWarningsInApp: true,
    freshnessWarningsEmail: false,
    dailyQuestInApp: true,
    dailyQuestEmail: false,
    streakRemindersInApp: true,
    streakRemindersEmail: false,
    productUpdatesInApp: true,
    productUpdatesEmail: true,
    marketingEmail: false
  });

  // Privacy & Data state
  const [searchHistoryEnabled, setSearchHistoryEnabled] = useState<boolean>(
    () => localStorage.getItem("biznearyou_history_enabled") !== "false"
  );
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(
    () => localStorage.getItem("biznearyou_analytics_enabled") !== "false"
  );

  // Accessibility state
  const [highContrast, setHighContrast] = useState<boolean>(
    () => localStorage.getItem("biznearyou_high_contrast") === "true"
  );

  // Live Stripe Pricing state
  const [stripePrices, setStripePrices] = useState<{
    monthlyFormatted: string | null;
    yearlyFormatted: string | null;
    isLoading: boolean;
    isConfirmed: boolean;
  }>({
    monthlyFormatted: null,
    yearlyFormatted: null,
    isLoading: true,
    isConfirmed: false
  });

  // Live Supabase User & Connected Providers state
  const [liveUser, setLiveUser] = useState<any>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isUnlinkingProvider, setIsUnlinkingProvider] = useState<string | null>(null);

  useEffect(() => {
    async function loadLiveUser() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setLiveUser(user);
        }
      } catch (e) {
        console.warn("Failed to fetch live Supabase user for settings:", e);
      }
    }
    loadLiveUser();
  }, [currentUser]);

  const identities = liveUser?.identities || currentUser?.identities || [];
  const appProviders = liveUser?.app_metadata?.providers || currentUser?.app_metadata?.providers || [currentUser?.provider || "email"];
  const currentProvider = liveUser?.app_metadata?.provider || currentUser?.provider || "email";

  const isGoogleConnected = appProviders.includes("google") || identities.some((i: any) => i.provider === "google") || currentProvider === "google";
  const isGithubConnected = appProviders.includes("github") || identities.some((i: any) => i.provider === "github") || currentProvider === "github";
  const isEmailConnected = appProviders.includes("email") || identities.some((i: any) => i.provider === "email") || currentProvider === "email";

  const googleIdentity = identities.find((i: any) => i.provider === "google");
  const githubIdentity = identities.find((i: any) => i.provider === "github");

  const totalConnectedCount = (isGoogleConnected ? 1 : 0) + (isGithubConnected ? 1 : 0) + (isEmailConnected ? 1 : 0);

  const handleUnlink = async (providerName: string, identityObj: any) => {
    if (totalConnectedCount <= 1) {
      showToast("You cannot disconnect your only active sign-in method.", "error");
      return;
    }
    if (!identityObj) {
      showToast(`No linked ${providerName} identity record found to unlink.`, "error");
      return;
    }
    if (!window.confirm(`Are you sure you want to disconnect your ${providerName} account?`)) {
      return;
    }

    setIsUnlinkingProvider(providerName);
    try {
      const { error } = await supabase.auth.unlinkIdentity(identityObj);
      if (error) throw error;
      showToast(`${providerName} account disconnected successfully`, "success");

      // Refresh live user
      const { data: { user: updatedUser } } = await supabase.auth.getUser();
      if (updatedUser) {
        setLiveUser(updatedUser);
      }
    } catch (err: any) {
      console.error("Unlink error:", err);
      showToast(err.message || `Failed to disconnect ${providerName}`, "error");
    } finally {
      setIsUnlinkingProvider(null);
    }
  };

  // Admin states
  const [adminLookupQuery, setAdminLookupQuery] = useState("");

  // Load User Settings from Supabase on mount
  useEffect(() => {
    let isMounted = true;
    apiFetch("/api/user-settings")
      .then((data) => {
        if (!isMounted || !data) return;
        if (data.full_name !== undefined) setFullName(data.full_name);
        if (data.display_name !== undefined) setDisplayName(data.display_name);
        if (data.audience_mode) handleSelectAudienceMode(data.audience_mode as AudienceModeId);
        if (data.default_zipcode !== undefined) setSettingsDefaultZipcode(data.default_zipcode);
        if (data.search_radius) setSearchRadius(data.search_radius);
        if (data.distance_unit) setDistanceUnit(data.distance_unit);
        if (data.travel_mode) setTravelMode(data.travel_mode);
        if (data.default_sort) setDefaultSort(data.default_sort);
        if (data.auto_apply_preferences !== undefined) setAutoApplyPreferences(data.auto_apply_preferences);
        if (data.theme) handleSaveUserSettings(data.theme, mapStyle, settingsLanguage);
        if (data.map_style) onMapStyleChange(data.map_style);
        if (data.layout_density) setLayoutDensity(data.layout_density);
        if (data.show_phone_details !== undefined) setShowPhoneDetails(data.show_phone_details);
        if (data.show_website_opportunity !== undefined) setShowGapScore(data.show_website_opportunity);
        if (data.remember_collapsed !== undefined) setRememberCollapsed(data.remember_collapsed);
        if (data.batch_fetch_limit) setSettingsMaxResults(data.batch_fetch_limit);
        if (data.default_view_mode) setDefaultViewMode(data.default_view_mode);
        if (data.show_audience_score_badge !== undefined) setShowOpportunityScore(data.show_audience_score_badge);
        if (data.show_signal_badges !== undefined) setShowHiringSignals(data.show_signal_badges);
        if (data.show_low_confidence !== undefined) setShowLowConfidence(data.show_low_confidence);
        if (data.notifications) setNotifications((prev) => ({ ...prev, ...data.notifications }));
        if (data.tracking_enabled !== undefined) handleToggleTracking(data.tracking_enabled);
        if (data.reduced_motion !== undefined) setReducedMotion(data.reduced_motion);
        if (data.high_contrast !== undefined) setHighContrast(data.high_contrast);
      })
      .catch((err) => {
        console.warn("Could not load user_settings from Supabase:", err);
      });

    // Fetch live Stripe prices
    apiFetch("/api/billing/public-prices")
      .then((res) => {
        if (!isMounted || !res) return;
        const mFormat = res.monthly?.formatted;
        const yFormat = res.yearly?.formatted;
        if (mFormat && yFormat) {
          setStripePrices({
            monthlyFormatted: mFormat,
            yearlyFormatted: yFormat,
            isLoading: false,
            isConfirmed: true
          });
        } else {
          setStripePrices({
            monthlyFormatted: null,
            yearlyFormatted: null,
            isLoading: false,
            isConfirmed: false
          });
        }
      })
      .catch(() => {
        if (isMounted) {
          setStripePrices({
            monthlyFormatted: null,
            yearlyFormatted: null,
            isLoading: false,
            isConfirmed: false
          });
        }
      });

    return () => { isMounted = false; };
  }, []);

  // Update DOM classes for Accessibility and Appearance
  useEffect(() => {
    if (reducedMotion) {
      document.documentElement.classList.add("reduced-motion");
      localStorage.setItem("biznearyou_reduced_motion", "true");
    } else {
      document.documentElement.classList.remove("reduced-motion");
      localStorage.setItem("biznearyou_reduced_motion", "false");
    }
  }, [reducedMotion]);

  useEffect(() => {
    if (highContrast) {
      document.documentElement.classList.add("high-contrast");
      localStorage.setItem("biznearyou_high_contrast", "true");
    } else {
      document.documentElement.classList.remove("high-contrast");
      localStorage.setItem("biznearyou_high_contrast", "false");
    }
  }, [highContrast]);

  useEffect(() => {
    if (layoutDensity === "compact") {
      document.documentElement.classList.add("compact-mode");
      localStorage.setItem("biznearyou_compact_mode", "true");
    } else {
      document.documentElement.classList.remove("compact-mode");
      localStorage.setItem("biznearyou_compact_mode", "false");
    }
  }, [layoutDensity]);

  // Persist Partial Setting Updates to Supabase
  const updateSettingAndPersist = async (field: string, value: any, label?: string) => {
    try {
      await apiFetch("/api/user-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value })
      });
      if (label) {
        showToast(`${label} saved successfully`, "success");
      }
    } catch (err: any) {
      showToast(err.message || "Failed to save setting to database", "error");
      throw err;
    }
  };

  // Sync profile edits
  useEffect(() => {
    const origFull = currentUser?.user_metadata?.full_name || currentUser?.full_name || "";
    const origDisp = currentUser?.user_metadata?.display_name || currentUser?.display_name || "";
    setHasProfileChanges(fullName !== origFull || displayName !== origDisp);
  }, [fullName, displayName, currentUser]);

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await updateSettingAndPersist("full_name", fullName);
      await updateSettingAndPersist("display_name", displayName);
      showToast("Profile details updated successfully", "success");
      setHasProfileChanges(false);
    } catch (err: any) {
      showToast(err.message || "Failed to save profile details", "error");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Handle Account Deletion
  const handleDeleteAccountAction = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setIsDeletingAccount(true);
    setDeleteError(null);
    try {
      const res = await apiFetch("/api/account", { method: "DELETE" });
      if (res.ok) {
        setIsDeleteModalOpen(false);
        try {
          localStorage.clear();
        } catch (e) {
          console.error("Local storage error:", e);
        }
        handleLogout();
        showToast("Your account and all associated data have been deleted.", "success");
      } else {
        const data = await res.json().catch(() => ({}));
        setDeleteError(data.message || data.error || "Failed to delete account.");
      }
    } catch (err: any) {
      setDeleteError(err.message || "An unexpected error occurred.");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Launch Stripe Customer Portal
  const [isLaunchingPortal, setIsLaunchingPortal] = useState(false);
  const handleOpenStripePortal = async () => {
    setIsLaunchingPortal(true);
    try {
      const res = await apiFetch("/api/billing/customer-portal", { method: "POST" });
      if (res.url) {
        window.location.href = res.url;
      } else {
        showToast("Failed to generate billing portal URL", "error");
      }
    } catch (err: any) {
      showToast(err.message || "Billing portal is unavailable.", "error");
    } finally {
      setIsLaunchingPortal(false);
    }
  };

  // Data Actions
  const handleClearHistory = async () => {
    if (!confirm("Clear your search history? This will remove all recent location queries from history.")) return;
    try {
      await apiFetch("/api/history", { method: "DELETE" });
      showToast("Search history cleared successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to clear search history", "error");
    }
  };

  const handleResetPersonalization = async () => {
    if (!confirm("Reset personalization tracking data? This will restore neutral recommendations.")) return;
    try {
      await apiFetch("/api/location/tracking/reset", { method: "POST" });
      showToast("Personalization history reset successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to reset personalization history", "error");
    }
  };

  const handleExportDataCSV = () => {
    if (savedBusinesses.length === 0) {
      showToast("No saved businesses to export.", "info");
      return;
    }
    const headers = ["ID", "Name", "Category", "Address", "Phone", "Website", "Hiring Likely", "Score"];
    const rows = savedBusinesses.map((b) => [
      `"${b.id || ""}"`,
      `"${(b.name || "").replace(/"/g, '""')}"`,
      `"${(b.primary_category || "").replace(/"/g, '""')}"`,
      `"${(b.address || "").replace(/"/g, '""')}"`,
      `"${b.phone || ""}"`,
      `"${b.website || ""}"`,
      b.is_possibly_hiring ? "Yes" : "No",
      b.opportunity_score || ""
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `biznearyou_saved_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("Exported saved records to CSV", "success");
  };

  // List of Tabs with metadata
  const tabsList: {
    id: SettingsTabId;
    label: string;
    icon: React.ElementType;
    description: string;
    badge?: string;
    category: "Account" | "Preferences" | "Privacy & System" | "Admin";
  }[] = [
    {
      id: "account",
      label: "Account",
      icon: User,
      description: "Manage credentials, linked accounts, and account deletion.",
      category: "Account"
    },
    {
      id: "profile",
      label: "Profile & Preferences",
      icon: Sliders,
      description: "Default search location, radius, audience mode, and sorting.",
      category: "Preferences"
    },
    {
      id: "appearance",
      label: "Appearance",
      icon: Eye,
      description: "Theme selection, layout density, map style, and result card options.",
      category: "Preferences"
    },
    {
      id: "search",
      label: "Search & Discovery",
      icon: Search,
      description: "Best Match algorithm parameters, signal weights, and batch limits.",
      category: "Preferences"
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: Bell,
      description: "In-app & email notification channels for alerts and signals.",
      category: "Preferences"
    },
    {
      id: "privacy",
      label: "Privacy & Data",
      icon: ShieldCheck,
      description: "Personalization controls, search history logs, and data exports.",
      category: "Privacy & System"
    },
    {
      id: "billing",
      label: "Billing & Plan",
      icon: CreditCard,
      description: "Manage subscription, Stripe portal, and Pro tier features.",
      badge: isProUser ? "PRO" : "FREE",
      category: "Account"
    },
    {
      id: "data",
      label: "Saved Data & Organization",
      icon: Bookmark,
      description: "Overview of saved businesses, pipeline records, and export tools.",
      category: "Privacy & System"
    },
    {
      id: "accessibility",
      label: "Accessibility",
      icon: Layers,
      description: "Reduced motion, text scale, contrast, and navigation focus.",
      category: "Privacy & System"
    },
    {
      id: "help",
      label: "Help & Information",
      icon: HelpCircle,
      description: "Help center links, support contact, bug reports, and app version.",
      category: "Privacy & System"
    },
    ...(isAdmin
      ? [
          {
            id: "admin" as SettingsTabId,
            label: "Admin Controls",
            icon: ShieldAlert,
            description: "System health, feature flags, user lookup, and configuration.",
            badge: "ADMIN",
            category: "Admin" as const
          }
        ]
      : [])
  ];

  const activeTabMeta = tabsList.find((t) => t.id === activeTab) || tabsList[0];

  // Helper for toggle updates with localStorage and Supabase persistence
  const handleToggleSetting = (
    key: string,
    value: boolean,
    setter: (v: boolean) => void,
    label: string,
    dbField?: string
  ) => {
    const previousValue = !value;
    setter(value);
    localStorage.setItem(key, String(value));
    if (dbField) {
      updateSettingAndPersist(dbField, value, label).catch(() => {
        setter(previousValue);
        localStorage.setItem(key, String(previousValue));
      });
    } else {
      showToast(`${label} ${value ? "enabled" : "disabled"}`, "info");
    }
  };

  // Search Index for Quick Search Bar across settings
  const SETTINGS_INDEX = useMemo(() => [
    { tab: "account" as const, id: "full_name", name: "Full Name", description: "Account owner name", keywords: "name profile identity owner" },
    { tab: "account" as const, id: "display_name", name: "Display Name", description: "Public name in dashboard, reports & exports", keywords: "display name alias handle handle" },
    { tab: "account" as const, id: "email", name: "Email Address", description: "Primary account authentication email address", keywords: "email address login auth" },
    { tab: "account" as const, id: "connected_providers", name: "Connected Identity Providers", description: "Link Google or GitHub accounts", keywords: "google github oauth link identity auth" },
    { tab: "account" as const, id: "password_reset", name: "Password Reset", description: "Send password reset link to your email", keywords: "password reset security key" },
    { tab: "account" as const, id: "delete_account", name: "Delete Account", description: "Permanently delete workspace and user settings", keywords: "delete account erase remove destroy" },

    { tab: "profile" as const, id: "audience_mode", name: "Audience Mode", description: "Set sourcing mode (Local Biz Owner, Job Seeker, Sales, Agency)", keywords: "audience mode target persona lead" },
    { tab: "profile" as const, id: "default_zipcode", name: "Default Location / ZIP", description: "Default search ZIP code or city", keywords: "zip zipcode location city default area" },
    { tab: "profile" as const, id: "search_radius", name: "Default Search Radius", description: "Geographical search radius in miles/km", keywords: "radius distance range miles km" },
    { tab: "profile" as const, id: "distance_unit", name: "Distance Unit", description: "Toggle between Miles and Kilometers", keywords: "miles km kilometers unit measurement" },
    { tab: "profile" as const, id: "travel_mode", name: "Preferred Travel Mode", description: "Driving, walking, transit, biking", keywords: "travel mode driving walking transit commute" },
    { tab: "profile" as const, id: "default_sort", name: "Default Result Sorting", description: "Best match, distance, rating, freshness", keywords: "sort order ranking default" },
    { tab: "profile" as const, id: "auto_apply_preferences", name: "Auto-Apply Preferences", description: "Auto-fill new searches with default location & radius", keywords: "auto apply filter preferences default" },

    { tab: "appearance" as const, id: "theme", name: "Workspace Visual Theme", description: "System Auto, Light, or Dark mode", keywords: "theme dark light auto appearance color" },
    { tab: "appearance" as const, id: "map_style", name: "Interactive Map Styling", description: "Standard, Satellite, Terrain, Dark Canvas, Retro", keywords: "map style satellite terrain dark canvas" },
    { tab: "appearance" as const, id: "layout_density", name: "Layout Spacing Density", description: "Comfortable vs. Compact layout density", keywords: "compact density padding layout" },
    { tab: "appearance" as const, id: "show_phone_details", name: "Show Phone Contact Details", description: "Toggle phone numbers on result cards", keywords: "phone contact phone details call" },
    { tab: "appearance" as const, id: "show_website_opportunity", name: "Website Opportunity Indicators", description: "Highlight missing mobile/contact form indicators", keywords: "website opportunity audit score gap" },
    { tab: "appearance" as const, id: "remember_collapsed", name: "Remember Collapsed Sections", description: "Persist section expand/collapse state", keywords: "collapsed sections drawer" },

    { tab: "search" as const, id: "batch_fetch_limit", name: "Batch Fetch Limit", description: "Set business results per batch (10-50)", keywords: "batch fetch limit results count" },
    { tab: "search" as const, id: "default_view_mode", name: "Default View Mode", description: "Toggle between List View and Map View", keywords: "list view map view layout" },
    { tab: "search" as const, id: "show_audience_score_badge", name: "Audience Score Badge", description: "Show/hide match score badges", keywords: "score badge match lead" },
    { tab: "search" as const, id: "show_signal_badges", name: "Signal Badges", description: "Display hiring, gap, and tech signal badges", keywords: "signals hiring signals badges" },
    { tab: "search" as const, id: "show_low_confidence", name: "Show Low-Confidence Results", description: "Toggle low-confidence results without deleting", keywords: "low confidence probability filter" },

    { tab: "notifications" as const, id: "notifications_opp", name: "Opportunity Alerts", description: "In-app and email opportunity alerts", keywords: "opportunity alert matches notification" },
    { tab: "notifications" as const, id: "notifications_hiring", name: "Hiring Signal Alerts", description: "Alerts for new hiring signals on targets", keywords: "hiring signals recruitment alert" },
    { tab: "notifications" as const, id: "notifications_saved", name: "Saved-Business Updates", description: "Alerts when saved businesses update", keywords: "saved updates crm alerts" },

    { tab: "privacy" as const, id: "tracking_enabled", name: "Personalization Tracker", description: "Machine-learning tracking toggle", keywords: "tracking ML personalization AI history" },
    { tab: "privacy" as const, id: "clear_history", name: "Clear Search History", description: "Delete search history logs", keywords: "clear search history remove query" },
    { tab: "privacy" as const, id: "clear_personalization", name: "Clear Personalization Data", description: "Reset machine-learning interaction logs", keywords: "reset personalization tracker clear" },

    { tab: "billing" as const, id: "billing_plan", name: "Subscription Plan & Pricing", description: "Current plan, Stripe pricing, and Customer Portal", keywords: "billing plan stripe price pro upgrade subscription" },

    { tab: "data" as const, id: "saved_records", name: "Saved Records Workspace", description: "Saved businesses count and CSV export", keywords: "saved records export csv dataset" },

    { tab: "accessibility" as const, id: "reduced_motion", name: "Reduced Motion", description: "Disable or reduce animations and transitions", keywords: "reduced motion animation transition" },
    { tab: "accessibility" as const, id: "high_contrast", name: "High Contrast Mode", description: "High contrast borders and enhanced text contrast", keywords: "high contrast visibility contrast text" },

    { tab: "help" as const, id: "documentation", name: "Documentation & Guides", description: "Guides, API reference, version info", keywords: "docs guide help support" },
    { tab: "help" as const, id: "support", name: "Contact Support", description: "Open support ticket or report issues", keywords: "support ticket email help" }
  ], []);

  const matchingSettings = useMemo(() => {
    const q = settingsSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return SETTINGS_INDEX.filter((item) => {
      return (
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.keywords.toLowerCase().includes(q) ||
        item.tab.toLowerCase().includes(q)
      );
    });
  }, [settingsSearchQuery, SETTINGS_INDEX]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans pb-24">
      {/* Top Banner Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2 font-medium">
            <button
              onClick={() => navigateTo("/dashboard")}
              className="hover:text-slate-900 dark:hover:text-white transition cursor-pointer"
            >
              Dashboard
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-900 dark:text-slate-100 font-bold">Settings</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-indigo-600 dark:text-indigo-400 font-bold">{activeTabMeta.label}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
                <SettingsIcon className="w-7 h-7 text-indigo-600 dark:text-indigo-400 rotate-45" />
                <span>BizNearYou Workspace Settings</span>
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
                Customize account profile, sourcing parameters, privacy controls, and subscription preferences.
              </p>
            </div>

            {/* Quick Search Bar across settings */}
            <div className="relative max-w-md w-full md:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={settingsSearchQuery}
                onChange={(e) => setSettingsSearchQuery(e.target.value)}
                placeholder="Search settings & controls..."
                className="w-full bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-8 py-2 text-xs font-semibold placeholder:text-slate-400 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              />
              {settingsSearchQuery && (
                <button
                  onClick={() => setSettingsSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}

              {/* Live Search Results Dropdown Overlay */}
              {settingsSearchQuery.trim() && (
                <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-50 overflow-hidden max-h-80 overflow-y-auto p-2 space-y-1">
                  <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase text-slate-400 tracking-wider flex justify-between items-center">
                    <span>Search Results ({matchingSettings.length})</span>
                    <span className="text-[9px] text-slate-400">Click to view control</span>
                  </div>
                  {matchingSettings.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-500 font-medium">
                      No settings found matching &quot;{settingsSearchQuery}&quot;
                    </div>
                  ) : (
                    matchingSettings.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          handleTabChange(item.tab);
                          setSettingsSearchQuery("");
                          setTimeout(() => {
                            const el = document.getElementById(`setting-${item.id}`);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "center" });
                              el.classList.add("ring-2", "ring-indigo-500", "rounded-xl");
                              setTimeout(() => el.classList.remove("ring-2", "ring-indigo-500", "rounded-xl"), 2500);
                            }
                          }, 150);
                        }}
                        className="w-full text-left p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 transition flex items-start justify-between gap-2 cursor-pointer group"
                      >
                        <div>
                          <div className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 flex items-center gap-1.5">
                            <span>{item.name}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{item.description}</p>
                        </div>
                        <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 shrink-0">
                          {item.tab}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Settings Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Navigation Sidebar (Desktop) / Mobile Select Dropdown */}
          <aside className="lg:col-span-3 space-y-4">
            {/* Mobile Tab Selector */}
            <div className="block lg:hidden bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1.5">
                Select Settings Category
              </label>
              <div className="relative">
                <select
                  value={activeTab}
                  onChange={(e) => handleTabChange(e.target.value as SettingsTabId)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-900 dark:text-white outline-none appearance-none pr-8 cursor-pointer"
                >
                  {tabsList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label} {t.badge ? `(${t.badge})` : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Desktop Left Tab Navigation */}
            <div className="hidden lg:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-2">
              <div className="p-3 border-b border-slate-100 dark:border-slate-800/80 mb-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Settings Menu
                </span>
              </div>

              <div className="space-y-1">
                {tabsList.map((tab) => {
                  const IconComp = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                        isActive
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/20"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <IconComp className={`w-4 h-4 shrink-0 ${isActive ? "text-white" : "text-slate-400 dark:text-slate-500"}`} />
                        <span className="truncate">{tab.label}</span>
                      </div>
                      {tab.badge && (
                        <span
                          className={`text-[9px] font-extrabold px-1.5 py-0.2 rounded uppercase shrink-0 ${
                            isActive
                              ? "bg-white/20 text-white"
                              : tab.badge === "PRO"
                              ? "bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                              : tab.badge === "ADMIN"
                              ? "bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Sidebar Footer Info */}
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 px-3 pb-2 text-[10.5px] text-slate-400 font-medium">
                <p>BizNearYou Engine v3.0</p>
                <p className="mt-0.5 text-[9.5px]">User: {currentUser?.email || "Authenticated User"}</p>
              </div>
            </div>
          </aside>

          {/* Right Main Content Area */}
          <main className="lg:col-span-9 space-y-6">
            
            {/* Active Tab Banner */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 rounded-2xl border border-indigo-100 dark:border-indigo-900">
                  <activeTabMeta.icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                    <span>{activeTabMeta.label}</span>
                    {activeTabMeta.badge && (
                      <span className="text-[10px] font-black bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-md uppercase tracking-wider">
                        {activeTabMeta.badge}
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                    {activeTabMeta.description}
                  </p>
                </div>
              </div>
            </div>

            {/* TAB 1: ACCOUNT */}
            {activeTab === "account" && (
              <div className="space-y-6">
                
                {/* Account Credentials & Identity */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-500" />
                    <span>Identity & Profile Details</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div id="setting-full_name" className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Full Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="e.g. Alex Morgan"
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div id="setting-display_name" className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Display Name</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="e.g. AlexM"
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div id="setting-email" className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Email Address</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={currentUser?.email || ""}
                          disabled
                          className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 cursor-not-allowed"
                        />
                        <span className="shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Verified
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Account Member Since</label>
                      <input
                        type="text"
                        value={
                          currentUser?.created_at
                            ? new Date(currentUser.created_at).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric"
                              })
                            : currentUser?.joinedAt
                            ? new Date(currentUser.joinedAt).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric"
                              })
                            : new Date().toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric"
                              })
                        }
                        disabled
                        className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="pt-3 flex justify-end">
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isSavingProfile ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      <span>Save Settings</span>
                    </button>
                  </div>
                </div>

                {/* Linked Accounts & Security */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Key className="w-4 h-4 text-indigo-500" />
                    <span>Security & Connected Providers</span>
                  </h3>

                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                    Link social logins or reset your primary account password for enhanced account security.
                  </p>

                  <div className="space-y-3">
                    {/* Google Account */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 rounded-xl gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-900 dark:text-white">Google Account</span>
                            {isGoogleConnected && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                currentProvider === "google"
                                  ? "bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
                                  : "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                              }`}>
                                {currentProvider === "google" ? "Primary Sign-In Method" : "Connected"}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                            {googleIdentity?.identity_data?.email || (isGoogleConnected ? "Connected for single sign-on" : "Connect your Google account for easy sign in")}
                          </p>
                        </div>
                      </div>

                      <div className="shrink-0">
                        {isGoogleConnected ? (
                          totalConnectedCount > 1 && currentProvider !== "google" ? (
                            <button
                              type="button"
                              disabled={isUnlinkingProvider === "Google"}
                              onClick={() => handleUnlink("Google", googleIdentity)}
                              className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 text-xs font-bold rounded-lg transition border border-rose-200 dark:border-rose-800 cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                            >
                              {isUnlinkingProvider === "Google" && <RefreshCw className="w-3 h-3 animate-spin" />}
                              <span>Disconnect</span>
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-400 font-semibold italic">
                              {currentProvider === "google" ? "Active Session Method" : "Primary Identity"}
                            </span>
                          )
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleLinkIdentity("google")}
                            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition cursor-pointer shadow-2xs"
                          >
                            Connect Google Account
                          </button>
                        )}
                      </div>
                    </div>

                    {/* GitHub Account */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 rounded-xl gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5 shrink-0 fill-slate-900 dark:fill-white" viewBox="0 0 24 24">
                            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-900 dark:text-white">GitHub Account</span>
                            {isGithubConnected && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                currentProvider === "github"
                                  ? "bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
                                  : "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                              }`}>
                                {currentProvider === "github" ? "Primary Sign-In Method" : "Connected"}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                            {githubIdentity?.identity_data?.email || githubIdentity?.identity_data?.user_name || (isGithubConnected ? "Connected for developer sign on" : "Connect your GitHub account for easy sign in")}
                          </p>
                        </div>
                      </div>

                      <div className="shrink-0">
                        {isGithubConnected ? (
                          totalConnectedCount > 1 && currentProvider !== "github" ? (
                            <button
                              type="button"
                              disabled={isUnlinkingProvider === "GitHub"}
                              onClick={() => handleUnlink("GitHub", githubIdentity)}
                              className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 text-xs font-bold rounded-lg transition border border-rose-200 dark:border-rose-800 cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                            >
                              {isUnlinkingProvider === "GitHub" && <RefreshCw className="w-3 h-3 animate-spin" />}
                              <span>Disconnect</span>
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-400 font-semibold italic">
                              {currentProvider === "github" ? "Active Session Method" : "Primary Identity"}
                            </span>
                          )
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleLinkIdentity("github")}
                            className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold rounded-lg transition cursor-pointer shadow-2xs"
                          >
                            Connect GitHub Account
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Password Management</span>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        Request a password reset link to be sent to <strong className="text-slate-700 dark:text-slate-300">{currentUser?.email}</strong>.
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={isSendingReset}
                      onClick={async () => {
                        try {
                          setIsSendingReset(true);
                          const userEmail = currentUser?.email;
                          if (!userEmail) {
                            throw new Error("No active email address found for user account.");
                          }
                          const res = await sendPasswordResetEmail(userEmail);
                          showToast(res.message || `Password reset link sent to ${userEmail}`, "success");
                        } catch (e: any) {
                          showToast(e.message || "Failed to send reset email", "error");
                        } finally {
                          setIsSendingReset(false);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shrink-0 shadow-2xs disabled:opacity-50"
                    >
                      {isSendingReset ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Key className="w-3.5 h-3.5" />
                      )}
                      <span>Send Password Reset</span>
                    </button>
                  </div>
                </div>

                {/* Session Logout & Danger Zone */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-black text-slate-900 dark:text-white">Active Session Controls</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Log out of your current workspace session on this browser.</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer"
                    >
                      <LogOut className="w-3.5 h-3.5 text-slate-500" />
                      <span>Log Out of Workspace</span>
                    </button>
                  </div>

                  {/* Danger Zone Box */}
                  <div className="pt-6 border-t border-rose-100 dark:border-rose-950 space-y-3">
                    <h4 className="text-xs font-black text-rose-600 uppercase tracking-widest flex items-center gap-1.5">
                      <ShieldAlert className="w-4 h-4 text-rose-600" />
                      <span>Danger Zone</span>
                    </h4>

                    <div className="p-4 bg-rose-50/70 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900/60 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <span className="text-xs font-black text-rose-950 dark:text-rose-200 block">Permanently Delete Account</span>
                        <p className="text-[11px] text-rose-700 dark:text-rose-300 leading-relaxed max-w-xl font-medium">
                          Permanently delete your account, saved businesses, contacts, search history, custom CRM pipelines, quest progress, XP, badges, streaks, machine-learning preferences, and audience settings. Any active Stripe subscription will be canceled immediately. This action cannot be undone.
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setDeleteConfirmText("");
                          setDeleteError(null);
                          setIsDeleteModalOpen(true);
                        }}
                        className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow-rose-600/20 cursor-pointer transition shrink-0"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 2: PROFILE AND PREFERENCES */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                
                {/* Audience Experience Mode */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <div>
                    <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider flex items-center gap-2">
                      <Zap className="w-4 h-4 text-indigo-500" />
                      <span>Active Audience Sourcing Mode</span>
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Selecting an audience mode adapts signal weightings, lead score badges, and opportunity filters for your specific workflow.
                    </p>
                  </div>

                  <AudienceSelector
                    currentMode={audienceMode}
                    onSelectMode={(mode) => {
                      handleSelectAudienceMode(mode);
                      showToast(`Audience Mode set to ${AUDIENCE_MODES[mode]?.label || mode}`, "info");
                    }}
                  />
                </div>

                {/* Default Sourcing Parameters */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-500" />
                    <span>Default Location & Search Parameters</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Default Zip Code / Location</label>
                      <input
                        type="text"
                        value={settingsDefaultZipcode}
                        onChange={(e) => {
                          setSettingsDefaultZipcode(e.target.value);
                          localStorage.setItem("settings_default_zipcode", e.target.value);
                        }}
                        placeholder="e.g. 90210 or Los Angeles"
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-[10px] text-slate-400">Auto-fills search input when starting fresh queries.</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Default Search Radius</label>
                      <select
                        value={searchRadius}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setSearchRadius(val);
                          localStorage.setItem("biznearyou_search_radius", String(val));
                          showToast(`Search radius set to ${val} miles`, "info");
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value={5}>5 Miles</option>
                        <option value={10}>10 Miles</option>
                        <option value={15}>15 Miles (Default)</option>
                        <option value={25}>25 Miles</option>
                        <option value={50}>50 Miles (Expanded)</option>
                      </select>
                      <p className="text-[10px] text-slate-400">Baseline geographical radius for nearby businesses.</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Distance Unit</label>
                      <select
                        value={distanceUnit}
                        onChange={(e) => {
                          const val = e.target.value as "miles" | "km";
                          setDistanceUnit(val);
                          localStorage.setItem("biznearyou_distance_unit", val);
                          showToast(`Distance unit set to ${val}`, "info");
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="miles">Miles (mi)</option>
                        <option value="km">Kilometers (km)</option>
                      </select>
                      <p className="text-[10px] text-slate-400">Preferred measurement unit for proximity scores.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Preferred Travel Mode</label>
                      <select
                        value={travelMode}
                        onChange={(e) => {
                          setTravelMode(e.target.value);
                          localStorage.setItem("biznearyou_travel_mode", e.target.value);
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="driving">🚗 Driving / Commute</option>
                        <option value="transit">🚌 Public Transit</option>
                        <option value="walking">🚶 Walking</option>
                        <option value="biking">🚴 Biking</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Default Result Sorting</label>
                      <select
                        value={defaultSort}
                        onChange={(e) => {
                          setDefaultSort(e.target.value);
                          localStorage.setItem("biznearyou_default_sort", e.target.value);
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="best_match">🎯 Best Match (Multi-Signal)</option>
                        <option value="hiring_signal">🔥 Highest Hiring Likely Score</option>
                        <option value="distance">🚗 Closest Distance</option>
                        <option value="rating">⭐ Highest Google Rating</option>
                        <option value="freshness">⚡ Most Recently Updated</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Auto-Apply Preferences</span>
                      <span className="text-[11px] text-slate-500">Automatically apply saved location and filters when starting new searches.</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={autoApplyPreferences}
                        onChange={(e) =>
                          handleToggleSetting(
                            "biznearyou_auto_apply",
                            e.target.checked,
                            setAutoApplyPreferences,
                            "Auto-apply preferences"
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                    <button
                      onClick={() => {
                        localStorage.setItem("settings_default_zipcode", settingsDefaultZipcode);
                        localStorage.setItem("biznearyou_search_radius", String(searchRadius));
                        localStorage.setItem("biznearyou_distance_unit", distanceUnit);
                        localStorage.setItem("biznearyou_travel_mode", travelMode);
                        localStorage.setItem("biznearyou_default_sort", defaultSort);
                        updateSettingAndPersist("default_zipcode", settingsDefaultZipcode);
                        showToast("Search preferences saved successfully", "success");
                      }}
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>Save Preferences</span>
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 3: APPEARANCE */}
            {activeTab === "appearance" && (
              <div className="space-y-6">
                
                {/* Visual Theme */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider flex items-center gap-2">
                    <Sun className="w-4 h-4 text-indigo-500" />
                    <span>Workspace Visual Theme</span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Choose between system auto theme, crisp light theme, or low-glare dark theme. Auto mode adapts to your operating system preference.
                  </p>

                  <ThemeSelector
                    currentTheme={settingsAppTheme}
                    onThemeChange={(newTheme) => {
                      handleSaveUserSettings(newTheme, mapStyle, settingsLanguage);
                      showToast(`Visual theme set to ${newTheme}`, "info");
                    }}
                    variant="buttons"
                    idPrefix="settings-theme"
                  />
                </div>

                {/* Display & System Language */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider flex items-center gap-2">
                    <Globe className="w-4 h-4 text-indigo-500" />
                    <span>Display & System Language</span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Choose your preferred system language. Auto mode detects your browser's preferred language automatically.
                  </p>

                  <LanguageSelector
                    variant="pills"
                    idPrefix="settings-language"
                  />
                </div>

                {/* Map Style Preferences */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider flex items-center gap-2">
                    <Compass className="w-4 h-4 text-indigo-500" />
                    <span>Interactive Map Styling</span>
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { id: "roadmap", label: "Standard Roadmap", icon: MapPin },
                      { id: "satellite", label: "Satellite Imagery", icon: Globe },
                      { id: "hybrid", label: "Hybrid Satellite", icon: Layers },
                      { id: "terrain", label: "Terrain Topo", icon: Compass },
                      { id: "dark", label: "Dark Canvas Map", icon: Moon },
                      { id: "light", label: "Light Clean Map", icon: Sun }
                    ].map((m) => {
                      const isSelected = mapStyle === m.id;
                      const IconC = m.icon;
                      return (
                        <button
                          key={m.id}
                          onClick={() => {
                            onMapStyleChange(m.id);
                            handleSaveUserSettings(settingsAppTheme, m.id, settingsLanguage);
                            showToast(`Map style set to ${m.label}`, "info");
                          }}
                          className={`p-3 rounded-xl border text-left flex flex-col justify-between gap-2 transition cursor-pointer ${
                            isSelected
                              ? "bg-indigo-50 dark:bg-indigo-950/80 border-indigo-500 text-indigo-900 dark:text-indigo-200 font-bold shadow-xs"
                              : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-300"
                          }`}
                        >
                          <IconC className={`w-4 h-4 ${isSelected ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400"}`} />
                          <span className="text-xs font-bold">{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Layout Density & Display Toggles */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4 text-indigo-500" />
                    <span>Layout Density & Card Display Options</span>
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Compact Layout Density</span>
                        <span className="text-[11px] text-slate-500">Tightens padding and margins to display more business items per screen.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={layoutDensity === "compact"}
                          onChange={(e) => {
                            const isCompact = e.target.checked;
                            setLayoutDensity(isCompact ? "compact" : "comfortable");
                            localStorage.setItem("biznearyou_compact_mode", String(isCompact));
                            updateSettingAndPersist("compact_layout", isCompact, `Layout density set to ${isCompact ? "Compact" : "Comfortable"}`).catch(() => {
                              setLayoutDensity(isCompact ? "comfortable" : "compact");
                              localStorage.setItem("biznearyou_compact_mode", String(!isCompact));
                            });
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Show Phone Contact Details</span>
                        <span className="text-[11px] text-slate-500">Display phone numbers directly on search result cards.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showPhoneDetails}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_show_phone",
                              e.target.checked,
                              setShowPhoneDetails,
                              "Show phone details",
                              "show_contact_details"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Show Website Gap Opportunity Indicators</span>
                        <span className="text-[11px] text-slate-500">Highlight web design gaps, SSL status, and mobile audit scores.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showGapScore}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_show_gap",
                              e.target.checked,
                              setShowGapScore,
                              "Show website gaps",
                              "show_website_signals"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Remember Collapsed Sections</span>
                        <span className="text-[11px] text-slate-500">Persist collapsed filter drawers across sessions.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={rememberCollapsed}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_remember_collapsed",
                              e.target.checked,
                              setRememberCollapsed,
                              "Remember collapsed sections",
                              "remember_collapsed_sections"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 4: SEARCH AND DISCOVERY */}
            {activeTab === "search" && (
              <div className="space-y-6">
                
                {/* Best Match Explanation Card */}
                <div className="p-4 bg-indigo-50/80 dark:bg-indigo-950/40 rounded-2xl border border-indigo-100 dark:border-indigo-900/60 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <span className="text-xs font-black text-indigo-950 dark:text-indigo-200 block">
                      Best Match Ranking Mechanics
                    </span>
                    <p className="text-xs text-indigo-800 dark:text-indigo-300 leading-relaxed">
                      Best Match uses a multi-layered algorithm combining keyword relevance, signal freshness, physical proximity, and your active Audience Mode (e.g. {AUDIENCE_MODES[audienceMode]?.label || "Job Seeker"}). Protected ranking weights ensure fair, high-confidence results.
                    </p>
                  </div>
                </div>

                {/* Sourcing Controls & Limits */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Sliders className="w-4 h-4 text-indigo-500" />
                    <span>Search Batch & Display Controls</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Search Batch Fetch Limit</label>
                      <select
                        value={settingsMaxResults}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setSettingsMaxResults(val);
                          localStorage.setItem("settings_max_results", String(val));
                          showToast(`Search batch limit set to ${val}`, "info");
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value={10}>10 Businesses per Query</option>
                        <option value={15}>15 Businesses per Query (Default)</option>
                        <option value={20}>20 Businesses per Query</option>
                        <option value={30}>30 Businesses per Query</option>
                        <option value={50}>50 Businesses per Query (Dense)</option>
                      </select>
                      <p className="text-[10px] text-slate-400">Controls the number of records retrieved per search query.</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Default View Mode</label>
                      <select
                        value={defaultViewMode}
                        onChange={(e) => {
                          const val = e.target.value as "list" | "map";
                          setDefaultViewMode(val);
                          localStorage.setItem("biznearyou_default_view", val);
                          showToast(`Default view mode set to ${val.toUpperCase()}`, "info");
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="list">📋 List View First</option>
                        <option value="map">🗺️ Map Canvas First</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Show Opportunity Score Badge</span>
                        <span className="text-[11px] text-slate-500">Display calculated score out of 100 on result cards.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showOpportunityScore}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_show_opp_score",
                              e.target.checked,
                              setShowOpportunityScore,
                              "Show Opportunity Score",
                              "show_score_badge"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Show Hiring Signals</span>
                        <span className="text-[11px] text-slate-500">Display hiring indicators, career page audits, and hiring likely tags.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showHiringSignals}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_show_hiring_signals",
                              e.target.checked,
                              setShowHiringSignals,
                              "Show Hiring Signals",
                              "show_signal_badges"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Show Low-Confidence / Improbable Results</span>
                        <span className="text-[11px] text-slate-500">Include targets with limited public web presence in result list.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showLowConfidence}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_show_low_confidence",
                              e.target.checked,
                              setShowLowConfidence,
                              "Show low-confidence results",
                              "show_low_confidence_results"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 5: NOTIFICATIONS */}
            {activeTab === "notifications" && (
              <div className="space-y-6">
                
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-indigo-500" />
                    <span>In-App & Email Notification Preferences</span>
                  </h3>

                  <div className="space-y-4">
                    {[
                      { dbApp: "opp_alerts_in_app", dbEmail: "opp_alerts_email", keyApp: "oppAlertsInApp", keyEmail: "oppAlertsEmail", label: "Opportunity Alerts & Matches", desc: "Alerts when new businesses matching your saved criteria appear." },
                      { dbApp: "signals_in_app", dbEmail: "signals_email", keyApp: "hiringSignalsInApp", keyEmail: "hiringSignalsEmail", label: "New Audience-Relevant Signals", desc: "Notifications when saved targets show fresh recruitment or website activity." },
                      { dbApp: "saved_updates_in_app", dbEmail: "saved_updates_email", keyApp: "savedUpdatesInApp", keyEmail: "savedUpdatesEmail", label: "Saved-Business Updates", desc: "Changes to address, phone number, or web status for saved items." },
                      { dbApp: "freshness_in_app", dbEmail: "freshness_email", keyApp: "freshnessWarningsInApp", keyEmail: "freshnessWarningsEmail", label: "Signal Freshness Warnings", desc: "Alerts when cached data for a saved target requires recheck." },
                      { dbApp: "daily_quest_in_app", dbEmail: "daily_quest_email", keyApp: "dailyQuestInApp", keyEmail: "dailyQuestEmail", label: "Daily Discovery Quests", desc: "Reminders for daily discovery quests and streak bonuses." },
                      { dbApp: "product_updates_in_app", dbEmail: "product_updates_email", keyApp: "productUpdatesInApp", keyEmail: "productUpdatesEmail", label: "Product & Sourcing Updates", desc: "New feature announcements and platform updates." }
                    ].map((item, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800">
                        <div>
                          <span className="text-xs font-bold text-slate-900 dark:text-white block">{item.label}</span>
                          <span className="text-[11px] text-slate-500">{item.desc}</span>
                        </div>

                        <div className="flex items-center gap-4 shrink-0">
                          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={Boolean((notifications as any)[item.keyApp])}
                              onChange={(e) => {
                                const val = e.target.checked;
                                const oldVal = (notifications as any)[item.keyApp];
                                setNotifications((prev) => ({ ...prev, [item.keyApp]: val }));
                                updateSettingAndPersist(item.dbApp, val, `${item.label} (In-App)`).catch(() => {
                                  setNotifications((prev) => ({ ...prev, [item.keyApp]: oldVal }));
                                });
                              }}
                              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span>In-App</span>
                          </label>

                          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={Boolean((notifications as any)[item.keyEmail])}
                              onChange={(e) => {
                                const val = e.target.checked;
                                const oldVal = (notifications as any)[item.keyEmail];
                                setNotifications((prev) => ({ ...prev, [item.keyEmail]: val }));
                                updateSettingAndPersist(item.dbEmail, val, `${item.label} (Email)`).catch(() => {
                                  setNotifications((prev) => ({ ...prev, [item.keyEmail]: oldVal }));
                                });
                              }}
                              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span>Email</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mandatory Communications Info */}
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed flex items-start gap-3">
                    <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-slate-900 dark:text-white block mb-0.5">Mandatory System & Security Messages</span>
                      Account security alerts, email verification notices, Stripe billing receipts, and legally required privacy communications are always enabled for account safety.
                    </div>
                  </div>

                  <div className="pt-3 flex justify-end">
                    <button
                      onClick={() => showToast("Notification preferences updated and saved", "success")}
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>Save Notification Preferences</span>
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 6: PRIVACY AND DATA */}
            {activeTab === "privacy" && (
              <div className="space-y-6">
                
                {/* Personalization & ML Tracking */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-indigo-500" />
                    <span>Personalization & Machine Learning Controls</span>
                  </h3>

                  <div className="p-4 bg-slate-50 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <span className="text-xs font-black text-slate-900 dark:text-white block">Machine-Matching Tracker</span>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium">
                          Allow the system to log interaction patterns and physical location searches to dynamically personalize target recommendations. When disabled, searches remain strictly neutral.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={trackingEnabled}
                          onChange={(e) => handleToggleTracking(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Search History & Data Retention */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Database className="w-4 h-4 text-indigo-500" />
                    <span>Search History Logs & Data Hygiene</span>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={handleClearHistory}
                      className="p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-2xl text-left transition cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white">Clear Search History</span>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </div>
                      <p className="text-[11px] text-slate-500">Purge saved query logs and location search history from your profile.</p>
                    </button>

                    <button
                      onClick={handleResetPersonalization}
                      className="p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-2xl text-left transition cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white">Clear Personalization Data</span>
                        <RefreshCw className="w-4 h-4 text-indigo-500" />
                      </div>
                      <p className="text-[11px] text-slate-500">Reset recommendation weights back to default neutral settings.</p>
                    </button>
                  </div>

                  {/* Clarification Box */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/40 rounded-2xl border border-amber-200 dark:border-amber-800/60 text-xs text-amber-900 dark:text-amber-200 leading-relaxed font-medium flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold block">Clearing History vs Account Deletion</span>
                      Clearing search history or personalization logs resets recommendation tracking while keeping your account, saved businesses, and Pro subscription active. To permanently delete your entire workspace, visit the Account tab Danger Zone.
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 7: BILLING AND PLAN */}
            {activeTab === "billing" && (
              <div className="space-y-6">
                
                {/* Subscription Status Card */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Current Plan</span>
                      <h3 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2 mt-0.5">
                        <span>{isProUser ? "BizNearYou Pro Workspace" : "BizNearYou Free Plan"}</span>
                        <span
                          className={`text-xs font-black px-2.5 py-0.5 rounded-lg uppercase ${
                            isProUser
                              ? "bg-indigo-600 text-white shadow-xs"
                              : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                          }`}
                        >
                          {isProUser ? "Active Pro" : "Free Tier"}
                        </span>
                      </h3>
                    </div>

                    <div className="flex items-center gap-2">
                      {isProUser ? (
                        <button
                          onClick={handleOpenStripePortal}
                          disabled={isLaunchingPortal}
                          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          {isLaunchingPortal ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                          <span>Manage Subscription (Stripe Portal)</span>
                        </button>
                      ) : (
                        <button
                          onClick={onUpgradeClick}
                          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer"
                        >
                          <Zap className="w-4 h-4" />
                          <span>Upgrade to Pro</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Plan Features Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
                      <span className="text-xs font-black uppercase tracking-wider text-slate-500 block">Free Tier Limits</span>
                      <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 font-medium">
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Up to 15 business results per search query</li>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Basic hiring likelihood signal tags</li>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Interactive map canvas & location filters</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-indigo-50/70 dark:bg-indigo-950/40 rounded-2xl border border-indigo-200 dark:border-indigo-900/80 space-y-2">
                      <span className="text-xs font-black uppercase tracking-wider text-indigo-700 dark:text-indigo-300 block">Pro Features Included</span>
                      <ul className="text-xs text-indigo-900 dark:text-indigo-200 space-y-1.5 font-medium">
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> Unlimited CSV exports of saved targets</li>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> Outreach CRM & Business Intelligence</li>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> Dense batch Sourcing (up to 50 results)</li>
                      </ul>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 8: SAVED DATA AND ORGANIZATION */}
            {activeTab === "data" && (
              <div className="space-y-6">
                
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-indigo-500" />
                    <span>Saved Records & Organization Summary</span>
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                      <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 block">{savedBusinesses.length}</span>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Saved Businesses</span>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                      <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 block">Active</span>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">CRM Pipeline</span>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                      <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 block">Stored</span>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Search History</span>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                      <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 block">Enabled</span>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Export Tools</span>
                    </div>
                  </div>

                  <div className="pt-2 flex flex-wrap gap-3">
                    <button
                      onClick={() => navigateTo("/saved")}
                      className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition cursor-pointer flex items-center gap-2"
                    >
                      <Bookmark className="w-4 h-4" />
                      <span>Open Full Saved Records Workspace</span>
                    </button>

                    <button
                      onClick={handleExportDataCSV}
                      className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-white font-bold text-xs rounded-xl transition cursor-pointer flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export Saved Records (CSV)</span>
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 9: ACCESSIBILITY */}
            {activeTab === "accessibility" && (
              <div className="space-y-6">
                
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-500" />
                    <span>Accessibility & Navigation Improvements</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">Reduced Motion</span>
                        <span className="text-[11px] text-slate-500">Disable nonessential layout transitions and smooth animations.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={reducedMotion}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_reduced_motion",
                              e.target.checked,
                              setReducedMotion,
                              "Reduced motion"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">High Contrast Mode</span>
                        <span className="text-[11px] text-slate-500">Enhance border contrast and text legibility across workspace components.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={highContrast}
                          onChange={(e) =>
                            handleToggleSetting(
                              "biznearyou_high_contrast",
                              e.target.checked,
                              setHighContrast,
                              "High contrast mode"
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 10: HELP AND INFORMATION */}
            {activeTab === "help" && (
              <div className="space-y-6">
                
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-indigo-500" />
                    <span>Help Center & Support Resources</span>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => navigateTo("/docs")}
                      className="p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-2xl text-left transition cursor-pointer flex items-center justify-between"
                    >
                      <div>
                        <span className="text-xs font-bold text-slate-900 dark:text-white block">Documentation & Guide</span>
                        <span className="text-[11px] text-slate-500">Read platform sourcing methodologies and FAQ.</span>
                      </div>
                      <FileText className="w-5 h-5 text-indigo-500" />
                    </button>

                    <button
                      onClick={() => {
                        if (onContactSupportClick) onContactSupportClick();
                        else navigateTo("/contact");
                      }}
                      className="p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-2xl text-left transition cursor-pointer flex items-center justify-between"
                    >
                      <div>
                        <span className="text-xs font-bold text-slate-900 dark:text-white block">Contact Technical Support</span>
                        <span className="text-[11px] text-slate-500">Get direct help from our team.</span>
                      </div>
                      <Mail className="w-5 h-5 text-indigo-500" />
                    </button>
                  </div>

                  {/* App Version Info */}
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500">
                    <div>
                      <span className="font-bold text-slate-800 dark:text-slate-200">BizNearYou Workspace Engine</span>
                      <p className="text-[11px]">Version 3.0 • Sourcing & Intelligence Platform</p>
                    </div>
                    <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-[10px] font-mono font-bold">
                      BUILD 2026-07
                    </span>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 11: ADMIN (ADMIN ONLY) */}
            {activeTab === "admin" && isAdmin && (
              <div className="space-y-6">
                
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                  <h3 className="text-sm font-black uppercase text-rose-600 dark:text-rose-400 tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-rose-600" />
                    <span>Administrator Override Controls</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-black text-indigo-700 dark:text-indigo-300 block">Session Pro Override</span>
                        <span className="text-[11px] text-slate-500">Simulate Pro features for testing without changing account billing state.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={adminUsePro}
                          onChange={(e) => {
                            setAdminUsePro(e.target.checked);
                            showToast(`Admin Pro session override ${e.target.checked ? "enabled" : "disabled"}`, "info");
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-black text-rose-700 dark:text-rose-300 block">Bypass Paywall Restrictions</span>
                        <span className="text-[11px] text-slate-500">Bypass search limits for testing purposes.</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={settingsBypassPaywall}
                          onChange={(e) => {
                            setSettingsBypassPaywall(e.target.checked);
                            localStorage.setItem("settings_bypass_paywall", String(e.target.checked));
                            showToast(`Bypass paywall ${e.target.checked ? "enabled" : "disabled"}`, "info");
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </main>

        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl shadow-2xl border border-rose-200 dark:border-rose-900 max-w-md w-full overflow-hidden p-0 font-sans relative">
            <div className="h-1.5 bg-rose-600"></div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-rose-100 dark:bg-rose-950 text-rose-600 rounded-xl">
                  <ShieldAlert className="w-6 h-6 animate-pulse" />
                </div>
                <h3 className="font-black text-base tracking-tight text-slate-900 dark:text-white">
                  Permanently Delete Account?
                </h3>
              </div>

              <div className="space-y-3 text-xs text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
                <p>
                  This will permanently delete your BizNearYou account and purge all user data across all system databases. Any active Stripe subscription will be canceled immediately.
                </p>
                <div className="p-3 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-200 text-[11px] font-semibold rounded-xl">
                  Data to be purged: Saved businesses, CRM pipelines, search history, saved searches, quest progress, XP/badges/streaks, personalization preferences, and audience settings.
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <label className="text-[10.5px] font-black uppercase tracking-wider block text-slate-700 dark:text-slate-300">
                  Type <span className="text-rose-600 font-black">DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:border-rose-500 px-3.5 py-2.5 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none"
                />
              </div>

              {deleteError && (
                <div className="p-3 bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-200 text-xs font-bold rounded-xl">
                  ⚠️ {deleteError}
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeletingAccount}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-800 dark:text-white text-xs font-bold rounded-xl transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccountAction}
                disabled={deleteConfirmText !== "DELETE" || isDeletingAccount}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-xs font-bold rounded-xl shadow-md transition cursor-pointer flex items-center gap-1.5"
              >
                {isDeletingAccount && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>Confirm Permanent Deletion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
